import React from "react"
import PropTypes from "prop-types"
import {PieChart, Pie, Cell, ResponsiveContainer} from "recharts"
import {get} from "lodash"
import styled from "styled-components"
import {lighten} from "polished"

import {connect} from "@irati/store"
import {minWidthMedia} from "@irati/ui/src/global/media"
import {perkbox, greys} from "@irati/ui/src/global/colors"
import {Label1, Label4} from "@irati/ui/src/Labels"
import id from "@irati/utils/id"

import {H1Css, H4Css} from "../../ui/Headings"
import {IconTooltip} from "../../ui/Tooltip"
import {ChangeOverTime} from "../../ui/PillBadge"
import FlexWrapper from "../../ui/Layout/FlexWrapper"
import {COT_TOOLTIP_CONTEXT_DEFAULT, COT_TOOLTIP_CONTEXT_DRIVER} from "../../utils/constants/changeOverTime"

export const GRADIENT_BLUE = lighten(0.3, perkbox.blue)

const Wrapper = styled.div`
  height: 150px;
  display: inline-block;
  position: relative;
  border-radius: 15px;

  ${minWidthMedia.tablet`
    width: 290px;
    height: 180px;
    padding-top: 15px;
`}
`

const TextWrapper = styled.div`
  width: 217px;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  justify-content: space-between;

  ${minWidthMedia.tablet`
    width: 260px;
  `}
`

const MinMaxText = styled(Label1)`
  color: ${greys.medium};
`

const ScoreText = styled.div`
  display: flex;
  justify-content: center;
  transform: translate3d(0, -175%, 0);
`

const AverageScore = styled(Label4)`
  ${H1Css};
  align-self: flex-end;
  line-height: 54px;
`

const MaxScore = styled(Label4)`
  ${H4Css};
  color: ${greys.medium};
  align-self: flex-end;
  line-height: 26px;
`

const StyledTooltip = styled(IconTooltip)`
  margin-left: 3px;
  margin-bottom: 3px;
  align-self: flex-end;
`

const ChangeOverTimeWrapper = styled.div`
  padding: 10px 0 0 10px;
`

const Gauge = ({averageScore, historicalAverageScore, min, max, isPhone, tooltipText, isDriver, ...props}) => {
  const idNamespace = id("Gauge", props)
  const COLORS = ["url(#colorPv)", greys.light]
  const percentage = (averageScore / max) * 100
  const data = [{value: percentage}, {value: 100 - percentage}]
  return (
    <Wrapper>
      <ResponsiveContainer width="100%" height={isPhone ? 120 : 140}>
        <PieChart>
          <defs>
            <linearGradient id="colorPv" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={perkbox.blue} stopOpacity={1} />
              <stop offset="100%" stopColor={GRADIENT_BLUE} stopOpacity={1} />
            </linearGradient>
          </defs>
          <Pie
            dataKey="value"
            data={[data[0]]}
            cx="50%"
            cy={isPhone ? 110 : 130}
            innerRadius={isPhone ? 100 : 120}
            outerRadius={isPhone ? 110 : 130}
            startAngle={180}
            endAngle={0}
            fill={greys.light}
            isAnimationActive={false} />
          <Pie
            dataKey="value"
            data={data}
            cx="50%"
            cy={isPhone ? 110 : 130}
            innerRadius={isPhone ? 100 : 120}
            outerRadius={isPhone ? 110 : 130}
            startAngle={180}
            endAngle={0}
            fill="url(#colorPv)">
            {data.map((entry, index) => (
              <Cell key={index} fill={COLORS[index % COLORS.length]} strokeWidth={0} />
            ))}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
      <TextWrapper>
        <MinMaxText>{min}</MinMaxText>
        <MinMaxText>{max}</MinMaxText>
      </TextWrapper>
      <ScoreText>
        <AverageScore id={id("AverageScore", idNamespace)}>{averageScore.toFixed(1)}</AverageScore>
        <FlexWrapper direction="column" justify="space-between">
          <ChangeOverTimeWrapper>
            <ChangeOverTime
              contextContentId={isDriver ? COT_TOOLTIP_CONTEXT_DRIVER : COT_TOOLTIP_CONTEXT_DEFAULT}
              currentScore={averageScore}
              historicalScore={historicalAverageScore} />
          </ChangeOverTimeWrapper>
          <div>
            <MaxScore>/{max}</MaxScore>
            <StyledTooltip size={14}> {tooltipText} </StyledTooltip>
          </div>
        </FlexWrapper>
      </ScoreText>
    </Wrapper>
  )
}

Gauge.defaultProps = {
  min: 0,
  max: 5,
  averageScore: 0,
  historicalAverageScore: 0,
  isPhone: false,
  isDriver: false
}

Gauge.propTypes = {
  min: PropTypes.number,
  max: PropTypes.number,
  averageScore: PropTypes.number,
  isPhone: PropTypes.bool.isRequired,
  tooltipText: PropTypes.string.isRequired,
  historicalAverageScore: PropTypes.number,
  isDriver: PropTypes.bool
}
const mapStateToProps = ({mq}) => ({isPhone: get(mq, "lessThan.medium", false)})

export default connect(mapStateToProps)(Gauge)
